import {DataType} from '@wix/editor-platform-ooi-metadata-resolver';
import {createSettingsParams, SettingsParamType} from '@wix/tpa-settings';

const createTextMetadata =
  (translationKey: string) =>
  /* istanbul ignore next */
  ({t}: {t: (key: string) => string}) => ({
    type: DataType.text as const,
    defaultValue: {
      type: 'static' as const,
      value: t(translationKey),
    },
  });

export enum SettingsParamKeys {
  THANK_YOU_PAGE_TITLE = 'THANK_YOU_PAGE_TITLE',
  THANK_YOU_PAGE_TITLE_HTML_TAG = 'THANK_YOU_PAGE_TITLE_HTML_TAG',
  THANK_YOU_PAGE_SUBTITLE = 'THANK_YOU_PAGE_SUBTITLE',
  THANK_YOU_PAGE_SUBSCRIPTION_TITLE = 'THANK_YOU_PAGE_SUBSCRIPTION_TITLE',
  THANK_YOU_PAGE_SUBSCRIPTION_SUBTITLE = 'THANK_YOU_PAGE_SUBSCRIPTION_SUBTITLE',
  THANK_YOU_PAGE_ORDER_NUMBER_LABEL = 'THANK_YOU_PAGE_ORDER_NUMBER_LABEL',
  THANK_YOU_PAGE_TOTAL_COST_LABEL = 'THANK_YOU_PAGE_TOTAL_COST_LABEL',
  THANK_YOU_PAGE_SHIPPING_ADDRESS_LABEL = 'THANK_YOU_PAGE_SHIPPING_ADDRESS_LABEL',
  THANK_YOU_PAGE_CONTINUE_BROWSING_LINK_CAPTION = 'THANK_YOU_PAGE_CONTINUE_BROWSING_LINK_CAPTION',
  THANK_YOU_PAGE_CONTINUE_SHOPPING_LINK_OBJECT = 'THANK_YOU_PAGE_CONTINUE_SHOPPING_LINK_OBJECT',
  THANK_YOU_PAGE_BILLING_ADDRESS_LABEL = 'THANK_YOU_PAGE_BILLING_ADDRESS_LABEL',
  THANK_YOU_PAGE_PAYMENT_METHOD_LABEL = 'THANK_YOU_PAGE_PAYMENT_METHOD_LABEL',
}

export type ISettingsParams = {
  [SettingsParamKeys.THANK_YOU_PAGE_TITLE]: SettingsParamType.String;
  [SettingsParamKeys.THANK_YOU_PAGE_TITLE_HTML_TAG]: SettingsParamType.String;
  [SettingsParamKeys.THANK_YOU_PAGE_SUBTITLE]: SettingsParamType.String;
  [SettingsParamKeys.THANK_YOU_PAGE_SUBSCRIPTION_TITLE]: SettingsParamType.String;
  [SettingsParamKeys.THANK_YOU_PAGE_SUBSCRIPTION_SUBTITLE]: SettingsParamType.String;
  [SettingsParamKeys.THANK_YOU_PAGE_ORDER_NUMBER_LABEL]: SettingsParamType.String;
  [SettingsParamKeys.THANK_YOU_PAGE_TOTAL_COST_LABEL]: SettingsParamType.String;
  [SettingsParamKeys.THANK_YOU_PAGE_SHIPPING_ADDRESS_LABEL]: SettingsParamType.String;
  [SettingsParamKeys.THANK_YOU_PAGE_CONTINUE_BROWSING_LINK_CAPTION]: SettingsParamType.String;
  [SettingsParamKeys.THANK_YOU_PAGE_CONTINUE_SHOPPING_LINK_OBJECT]: SettingsParamType.Object;
  [SettingsParamKeys.THANK_YOU_PAGE_BILLING_ADDRESS_LABEL]: SettingsParamType.String;
  [SettingsParamKeys.THANK_YOU_PAGE_PAYMENT_METHOD_LABEL]: SettingsParamType.String;
};

export default createSettingsParams<ISettingsParams>({
  [SettingsParamKeys.THANK_YOU_PAGE_TITLE]: {
    type: SettingsParamType.String,
    getDefaultValue: () => null as unknown as string,
    inheritFromAppScope: true,
    metadata: createTextMetadata('thankYouPage.textTab.platformizedTitleDefaultLabel'),
  },
  [SettingsParamKeys.THANK_YOU_PAGE_TITLE_HTML_TAG]: {
    type: SettingsParamType.String,
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    getDefaultValue: () => null,
    inheritFromAppScope: true,
    // No builder metadata: this is the title's heading tag, not merchant text. A text param left out of
    // innerElements stays on the root element, and Edit text then opens on that empty root (ECP-14027).
  },
  [SettingsParamKeys.THANK_YOU_PAGE_SUBTITLE]: {
    type: SettingsParamType.String,
    getDefaultValue: ({t}) => t('thankyouPage.textTab.subtitleDefaultText'),
    inheritFromAppScope: true,
    metadata: createTextMetadata('thankyouPage.textTab.subtitleDefaultText'),
  },
  [SettingsParamKeys.THANK_YOU_PAGE_SUBSCRIPTION_TITLE]: {
    type: SettingsParamType.String,
    getDefaultValue: () => null as unknown as string,
    inheritFromAppScope: true,
    metadata: createTextMetadata('thankYouPage.textTab.subscription.title.defaultText'),
  },
  [SettingsParamKeys.THANK_YOU_PAGE_SUBSCRIPTION_SUBTITLE]: {
    type: SettingsParamType.String,
    getDefaultValue: ({t}) => t('thankYouPage.textTab.subscription.subtitle.defaultText'),
    inheritFromAppScope: true,
    metadata: createTextMetadata('thankYouPage.textTab.subscription.subtitle.defaultText'),
  },
  [SettingsParamKeys.THANK_YOU_PAGE_ORDER_NUMBER_LABEL]: {
    type: SettingsParamType.String,
    getDefaultValue: ({t}) => t('thankyouPage.textTab.orderNumberDefaultText'),
    inheritFromAppScope: true,
    metadata: createTextMetadata('thankyouPage.textTab.orderNumberDefaultText'),
  },
  [SettingsParamKeys.THANK_YOU_PAGE_TOTAL_COST_LABEL]: {
    type: SettingsParamType.String,
    getDefaultValue: ({t}) => t('thankyouPage.textTab.totalCostDefaultText'),
    inheritFromAppScope: true,
    metadata: createTextMetadata('thankyouPage.textTab.totalCostDefaultText'),
  },
  [SettingsParamKeys.THANK_YOU_PAGE_SHIPPING_ADDRESS_LABEL]: {
    type: SettingsParamType.String,
    getDefaultValue: () => null as unknown as string,
    inheritFromAppScope: true,
    metadata: createTextMetadata('thankYouPage.textTab.platformizedShippingAddressDefaultText'),
  },
  THANK_YOU_PAGE_CONTINUE_BROWSING_LINK_CAPTION: {
    type: SettingsParamType.String,
    getDefaultValue: ({t}) => t('thankyouPage.textTab.continueShoppingPlaceholder'),
    inheritFromAppScope: true,
    metadata: createTextMetadata('thankyouPage.textTab.continueShoppingPlaceholder'),
  },
  [SettingsParamKeys.THANK_YOU_PAGE_CONTINUE_SHOPPING_LINK_OBJECT]: {
    type: SettingsParamType.Object,
    // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
    getDefaultValue: () => null,
    inheritFromAppScope: true,
  },
  [SettingsParamKeys.THANK_YOU_PAGE_BILLING_ADDRESS_LABEL]: {
    type: SettingsParamType.String,
    getDefaultValue: ({t}) => t('thankYouPage.textTab.billingAddressDefaultText'),
    inheritFromAppScope: true,
    metadata: /* istanbul ignore next */ createTextMetadata('thankYouPage.textTab.billingAddressDefaultText'),
  },
  [SettingsParamKeys.THANK_YOU_PAGE_PAYMENT_METHOD_LABEL]: {
    type: SettingsParamType.String,
    getDefaultValue: ({t}) => t('thankYouPage.textTab.paymentMethodDefaultText'),
    inheritFromAppScope: true,

    metadata: /* istanbul ignore next */ createTextMetadata('thankYouPage.textTab.paymentMethodDefaultText'),
  },
});
